import { scaleLinear } from 'd3-scale';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ComposableMap, Geographies, Geography, ZoomableGroup } from 'react-simple-maps';

import indiaGeo from '@/assets/india-states.geo.json';
import { ZoomSlider } from '@/components/common/ZoomSlider';
import { INDIA_PROJECTION_CONFIG, geoStateName, type GeographyShape } from '@/lib/indiaGeo';
import { inr, type StateMetrics } from '@/lib/sales-metrics';
import cn from '@/utils/cn';

type Metric = 'leads' | 'clients' | 'products' | 'revenue';

// 1, not the old 0.4: the box now letterboxes the whole country into view at
// zoom 1 (see the sizing note on the map container), so anything below it only
// shrinks the map further into empty space - which is the complaint this change
// exists to fix.
const MIN_ZOOM = 1;
const MAX_ZOOM = 8;
const ZOOM_STEP = 0.25;
const HOME: [number, number] = INDIA_PROJECTION_CONFIG.center;

const clampZoom = (value: number): number => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, value));

const METRIC_META: Record<Metric, { label: string; color: string; format: (n: number) => string }> =
  {
    leads: { label: 'Leads', color: '#2563eb', format: (n) => `${n}` },
    clients: { label: 'Clients', color: '#16a34a', format: (n) => `${n}` },
    products: { label: 'Products', color: '#9333ea', format: (n) => `${n}` },
    revenue: { label: 'Revenue', color: '#ea580c', format: (n) => inr(n) },
  };

interface IndiaMapProps {
  data: StateMetrics[];
}

export function IndiaMap({ data }: IndiaMapProps) {
  const [metric, setMetric] = useState<Metric>('leads');
  const [hover, setHover] = useState<{ x: number; y: number; row: StateMetrics } | null>(null);

  // Zoom AND centre together, because Reset has to restore both - a reset that
  // only put the zoom back left a panned-away map at the right magnification
  // and the wrong place. `onMoveEnd` writes the position a drag or a wheel
  // landed on, so the readout always names where the map actually is rather
  // than the last value a button set.
  const [view, setView] = useState<{ zoom: number; center: [number, number] }>({
    zoom: MIN_ZOOM,
    center: HOME,
  });

  const zoomTo = useCallback((next: number): void => {
    setView((prev) => ({ ...prev, zoom: clampZoom(next) }));
  }, []);

  const resetView = useCallback((): void => {
    setView({ zoom: MIN_ZOOM, center: HOME });
  }, []);

  // useCallback is load-bearing, not tidiness. react-simple-maps lists
  // `onMoveEnd` in the dependency array of the effect that builds the d3 zoom
  // behaviour and calls `svg.call(zoom)` (see useZoomPan in its dist). An inline
  // arrow is a new identity every render, so the whole behaviour was being
  // reconstructed and rebound on each one - including every `setHover` fired by
  // a mousemove across a state path, which is the most frequent render there is
  // on this component.
  const handleMoveEnd = useCallback(
    (position: { coordinates: [number, number]; zoom: number }): void => {
      setView({ zoom: position.zoom, center: position.coordinates });
    },
    [],
  );

  const byState = useMemo(() => {
    const map = new Map<string, StateMetrics>();
    data.forEach((d) => map.set(d.state, d));
    return map;
  }, [data]);

  const max = Math.max(1, ...data.map((d) => d[metric]));
  const colorScale = scaleLinear<string>()
    .domain([0, max])
    .range(['#f1f5f9', METRIC_META[metric].color]);

  const totals = data.reduce(
    (acc, d) => ({
      leads: acc.leads + d.leads,
      clients: acc.clients + d.clients,
      products: acc.products + d.products,
      revenue: acc.revenue + d.revenue,
    }),
    {
      leads: 0,
      clients: 0,
      products: 0,
      revenue: 0,
    },
  );

  useEffect(() => {
    if (!hover) return undefined;
    const clear = (): void => setHover(null);
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setHover(null);
    };
    document.addEventListener('pointerdown', clear);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', clear);
      document.removeEventListener('keydown', onKey);
    };
  }, [hover]);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-1.5">
          {(Object.keys(METRIC_META) as Metric[]).map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setMetric(m)}
              className={cn(
                'rounded-md border px-2.5 py-1 text-xs font-medium transition',
                metric === m
                  ? 'border-primary bg-primary text-primary-foreground shadow-sm'
                  : 'border-border bg-background text-muted-foreground hover:bg-secondary hover:text-foreground',
              )}
            >
              {METRIC_META[m].label}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap gap-3 text-[11px] text-muted-foreground">
          <Stat label="Leads" value={String(totals.leads)} />
          <Stat label="Clients" value={String(totals.clients)} />
          <Stat label="Products" value={String(totals.products)} />
          <Stat label="Revenue" value={inr(totals.revenue)} />
        </div>
      </div>

      {/*
        FULL-WIDTH BOX, height-capped (Fix: the map was stranded at 600px on
        every screen while sitting in a card 1000px+ across).

        Two sizing regimes, because one does not fit both ends:

        - Below `lg` the box is WIDTH-driven (`aspect-[600/520]`). It fills the
          card and derives its height from the viewBox, so there is no wasted
          space around the map - which matters most on the screens that have
          none to waste. On a tablet that alone takes the map from 600px to the
          ~700px the card actually offers.

        - From `lg` the box is HEIGHT-capped (`aspect-auto` + an explicit
          height) and spans the full card width. Aspect-sizing a 1080px-wide
          card would render a 936px-tall map and bury the rest of the dashboard,
          so height is what has to be bounded here. The 600x520 viewBox
          letterboxes into the box (default preserveAspectRatio), which leaves a
          margin either side - that margin is where the zoom slider sits, the
          same arrangement the MIS maps use.

        `min(76vh, 760px)`: 76vh keeps the whole map on one screen on a laptop,
        and the 760px ceiling stops a tall monitor from stretching it into a
        band of empty card. The SVG is `height: 100%` for both regimes; below
        `lg` the aspect box makes that identical to the old `height: auto`.
      */}
      <div className="relative aspect-[600/520] w-full overflow-hidden rounded-lg border bg-card lg:aspect-auto lg:h-[min(76vh,760px)]">
        <ComposableMap
          projection="geoMercator"
          projectionConfig={INDIA_PROJECTION_CONFIG}
          width={600}
          height={520}
          style={{ width: '100%', height: '100%' }}
        >
          <ZoomableGroup
            zoom={view.zoom}
            center={view.center}
            minZoom={MIN_ZOOM}
            maxZoom={MAX_ZOOM}
            // Writing back the position the gesture already landed on, so this
            // cannot loop: the props come out equal to the transform that is
            // already applied and nothing re-renders a second time.
            onMoveEnd={handleMoveEnd}
          >
            <Geographies geography={indiaGeo as unknown as object}>
              {({ geographies }: { geographies: GeographyShape[] }) =>
                geographies.map((geo) => {
                  const stateName = geoStateName(geo.properties);
                  const row = byState.get(stateName);
                  const value = row ? row[metric] : 0;
                  const fill = value > 0 ? colorScale(value) : '#f8fafc';
                  return (
                    <Geography
                      key={geo.rsmKey}
                      geography={geo}
                      fill={fill}
                      stroke="#cbd5e1"
                      strokeWidth={0.5}
                      role="button"
                      onKeyDown={(e) => {
                        if (e.key !== 'Enter' && e.key !== ' ') return;
                        e.preventDefault();
                        const r = e.currentTarget.getBoundingClientRect();
                        setHover({
                          x: r.left + r.width / 2,
                          y: r.top,
                          row: row ?? {
                            state: stateName,
                            leads: 0,
                            clients: 0,
                            products: 0,
                            revenue: 0,
                          },
                        });
                      }}
                      aria-label={`${stateName}: ${METRIC_META[metric].format(value)} ${METRIC_META[metric].label}`}
                      onClick={(e) =>
                        setHover({
                          x: e.clientX,
                          y: e.clientY,
                          row: row ?? {
                            state: stateName,
                            leads: 0,
                            clients: 0,
                            products: 0,
                            revenue: 0,
                          },
                        })
                      }
                      onFocus={(e) => {
                        const r = e.currentTarget.getBoundingClientRect();
                        setHover({
                          x: r.left + r.width / 2,
                          y: r.top,
                          row: row ?? {
                            state: stateName,
                            leads: 0,
                            clients: 0,
                            products: 0,
                            revenue: 0,
                          },
                        });
                      }}
                      onBlur={() => setHover(null)}
                      onMouseEnter={(e) =>
                        setHover({
                          x: e.clientX,
                          y: e.clientY,
                          row: row ?? {
                            state: stateName,
                            leads: 0,
                            clients: 0,
                            products: 0,
                            revenue: 0,
                          },
                        })
                      }
                      onMouseMove={(e) =>
                        setHover((prev) => (prev ? { ...prev, x: e.clientX, y: e.clientY } : prev))
                      }
                      onMouseLeave={() => setHover(null)}
                      style={{
                        default: { outline: 'none', cursor: 'pointer' },
                        hover: { outline: 'none', fill: METRIC_META[metric].color, opacity: 0.85 },
                        pressed: { outline: 'none' },
                      }}
                    />
                  );
                })
              }
            </Geographies>
          </ZoomableGroup>
        </ComposableMap>

        {/* The same control the MIS maps and every MIS chart use, so zoom
            behaves and reads identically across the app - step in, drag, step
            out, reset, with a readout of where you are. It replaces a bare
            +/- pair that had no reset and no sense of position.

            Centred against the map rather than pinned to the top corner: from
            `lg` it lands in the letterbox margin beside the map instead of on
            top of it. `size="default"` (a 128px track) rather than the MIS
            map's "tall": this box is as short as ~300px on a phone, and a
            416px track would not fit in it. */}
        <ZoomSlider
          className="absolute right-2 top-1/2 z-10 -translate-y-1/2"
          value={view.zoom}
          min={MIN_ZOOM}
          max={MAX_ZOOM}
          step={ZOOM_STEP}
          onChange={zoomTo}
          onReset={resetView}
          readout={`${view.zoom.toFixed(1)}x`}
          caption="Map"
          label="Zoom level"
          resetLabel="Fit the whole map on screen"
        />

        <div className="absolute bottom-2 left-2 flex items-center gap-2 rounded-md border bg-background/90 px-2 py-1 text-[10px] text-muted-foreground backdrop-blur">
          <span>0</span>
          <span
            className="inline-block h-2 w-24 rounded"
            style={{
              background: `linear-gradient(to right, #f1f5f9, ${METRIC_META[metric].color})`,
            }}
          />
          <span className="tabular-nums">{METRIC_META[metric].format(max)}</span>
        </div>
      </div>

      {hover && (
        <div
          className="pointer-events-none fixed z-50 rounded-md border bg-popover px-3 py-2 text-xs shadow-lg"
          style={{
            left: Math.max(8, Math.min(hover.x + 12, window.innerWidth - 300)),
            top: Math.max(8, Math.min(hover.y + 12, window.innerHeight - 150)),
          }}
        >
          <div className="mb-1 font-semibold text-foreground">{hover.row.state}</div>
          <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-muted-foreground">
            <span>Leads</span>
            <span className="text-right tabular-nums text-foreground">{hover.row.leads}</span>
            <span>Clients</span>
            <span className="text-right tabular-nums text-foreground">{hover.row.clients}</span>
            <span>Products</span>
            <span className="text-right tabular-nums text-foreground">{hover.row.products}</span>
            <span>Revenue</span>
            <span className="text-right tabular-nums text-foreground">
              {inr(hover.row.revenue)}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <span className="inline-flex items-baseline gap-1">
      <span className="uppercase tracking-wider">{label}</span>
      <span className="font-semibold tabular-nums text-foreground">{value}</span>
    </span>
  );
}
